import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Button } from './button';
import { 
  ChevronLeft, 
  ChevronRight, 
  ChevronsLeft, 
  ChevronsRight,
  Shield,
  CheckCircle,
  XCircle,
  User
} from 'lucide-react';

/**
 * Componente de tabla de datos reutilizable
 * Soporta paginación, búsqueda, filtros y acciones
 */
const DataTable = ({
  section = '',
  data = [],
  columns = [],
  loading = false,
  searchTerm = '',
  onSearchChange,
  onSearch,
  searchPlaceholder = 'Buscar...',
  showSearch = true,
  showFilters = false,
  pagination = null,
  onPageChange,
  actions = [],
  emptyMessage = 'No hay datos disponibles',
  className = ''
}) => {
  // Asegurar que data sea siempre un array
  const safeData = Array.isArray(data) ? data : [];
  // Renderizar celda de datos
  const renderCell = (item, column) => {
    if (column.render) {
      return column.render(item, column.key);
    }

    const value = item[column.key];
    
    // Renderizado especial para tipos específicos
    switch (column.type) {
      case 'boolean':
        return value ? (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Activo
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" />
            Inactivo
          </span>
        );
      
      case 'role':
        const isAdmin = value === 'admin';
        return (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            isAdmin ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
          }`}>
            {isAdmin ? <Shield className="w-3 h-3 mr-1" /> : <User className="w-3 h-3 mr-1" />}
            {isAdmin ? 'Administrador' : 'Usuario'}
          </span>
        );
      
      case 'date':
        return new Date(value).toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      
      default:
        return value;
    }
  };

  // Renderizar acciones
  const renderActions = (item) => {
    return (
      <div className="flex items-center gap-1">
        {actions.map((action, index) => {
          // Verificar si la acción debe mostrarse
          if (action.show && !action.show(item)) {
            return null;
          }

          const IconComponent = action.icon;
          
          return (
            <Button
              key={index}
              onClick={() => action.onClick(item)}
              size="sm"
              variant={action.variant || "outline"}
              className={action.className || ""}
              title={action.title || action.label}
            >
              {IconComponent && <IconComponent className="w-4 h-4" />}
              {action.label && <span className="ml-1">{action.label}</span>}
            </Button>
          );
        })}
      </div>
    );
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{section}</span>
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              Cargando...
            </div>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>

        {/* Tabla */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-gray-200">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      column.className || ''
                    }`}
                  >
                    {column.label}
                  </th>
                ))}
                {actions.length > 0 && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {safeData.length === 0 ? (
                <tr>
                  <td 
                    colSpan={columns.length + (actions.length > 0 ? 1 : 0)}
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        Cargando datos...
                      </div>
                    ) : (
                      emptyMessage
                    )}
                  </td>
                </tr>
              ) : (
                safeData.map((item, index) => (
                  <tr 
                    key={item.id || index}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={`px-4 py-3 text-sm text-gray-900 ${
                          column.className || ''
                        }`}
                      >
                        {renderCell(item, column)}
                      </td>
                    ))}
                    {actions.length > 0 && (
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {renderActions(item)}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {pagination && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Mostrando {pagination.from} a {pagination.to} de {pagination.total} resultados
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => onPageChange?.(1)}
                disabled={pagination.currentPage === 1}
                variant="outline"
                size="sm"
              >
                <ChevronsLeft className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => onPageChange?.(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                variant="outline"
                size="sm"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-gray-700">
                Página {pagination.currentPage} de {pagination.lastPage}
              </span>
              <Button
                onClick={() => onPageChange?.(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.lastPage}
                variant="outline"
                size="sm"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => onPageChange?.(pagination.lastPage)}
                disabled={pagination.currentPage === pagination.lastPage}
                variant="outline"
                size="sm"
              >
                <ChevronsRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DataTable; 